import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, RefreshCw, Search, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, ExternalLink, Copy, Check, AlertCircle, CheckCircle, Clock, Zap, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface KieLog {
    id: string;
    task_id: string;
    log_id: string | null;
    event_type: "callback" | "poll" | "submit";
    raw_payload: any;
    extracted_status: string | null;
    extracted_url: string | null;
    error_message: string | null;
    created_at: string;
    generation_log?: {
        prompt: string;
        model: string;
        status: string;
        user_id?: string;
    };
}

const LOGS_PER_PAGE = 30;

const KieLogs = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { toast } = useToast();
    const [logs, setLogs] = useState<KieLog[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState(searchParams.get("search") || "");
    const [filterType, setFilterType] = useState<string>("all");
    const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [recheckingAll, setRecheckingAll] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchLogs = async () => {
        setLoading(true);
        try {
            let query = supabase
                .from("kie_api_logs")
                .select(`
                    *,
                    generation_logs:log_id (
                        prompt,
                        model,
                        status,
                        user_id
                    )
                `);

            if (searchQuery) {
                // If searchQuery looks like a UUID, search log_id too
                const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(searchQuery);

                if (isUuid) {
                    query = query.or(`task_id.eq.${searchQuery},log_id.eq.${searchQuery}`);
                } else {
                    query = query.ilike('task_id', `%${searchQuery}%`);
                }
            }

            const { data, error } = await query
                .order("created_at", { ascending: false })
                .limit(searchQuery ? 100 : 200);

            if (error) throw error;

            // Map data to expected format
            const mappedLogs = (data || []).map((log: any) => ({
                ...log,
                generation_log: log.generation_logs
            }));

            setLogs(mappedLogs as any);
        } catch (error: any) {
            console.error("Error fetching KIE logs:", error);
            toast({
                title: "Error",
                description: "Failed to fetch KIE logs: " + error.message,
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchLogs();
        }, searchQuery ? 500 : 0); // Debounce search

        return () => clearTimeout(timer);
    }, [searchQuery]);

    const handleRecheckAllStuckJobs = async () => {
        setRecheckingAll(true);
        try {
            const response = await fetch(
                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/recheck-stuck-jobs`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                }
            );
            const data = await response.json();
            if (data.ok) {
                toast({
                    title: "Recheck Complete",
                    description: `Processed ${data.processed} stuck jobs`,
                });
                fetchLogs();
            } else {
                throw new Error(data.error || "Failed to recheck");
            }
        } catch (error: any) {
            toast({
                title: "Error",
                description: error.message || "Failed to recheck stuck jobs",
                variant: "destructive",
            });
        } finally {
            setRecheckingAll(false);
        }
    };

    const toggleExpand = (id: string) => {
        const newExpanded = new Set(expandedLogs);
        if (newExpanded.has(id)) {
            newExpanded.delete(id);
        } else {
            newExpanded.add(id);
        }
        setExpandedLogs(newExpanded);
    };

    const copyToClipboard = async (text: string, id: string) => {
        await navigator.clipboard.writeText(text);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const filteredLogs = logs.filter((log) => {
        const matchesType = filterType === "all" || log.event_type === filterType;
        return matchesType;
    });

    // Clear search and URL param
    const clearSearch = () => {
        setSearchQuery("");
        const newParams = new URLSearchParams(searchParams);
        newParams.delete("search");
        setSearchParams(newParams);
    };

    // Pagination
    const totalPages = Math.ceil(filteredLogs.length / LOGS_PER_PAGE);
    const startIndex = (currentPage - 1) * LOGS_PER_PAGE;
    const paginatedLogs = filteredLogs.slice(startIndex, startIndex + LOGS_PER_PAGE);

    // Reset to page 1 when filters change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterType]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleString("th-TH", {
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        });
    };

    const getEventBadge = (type: string) => {
        switch (type) {
            case "callback":
                return <Badge className="bg-blue-500/10 text-blue-500 border-blue-500/20">Callback</Badge>;
            case "poll":
                return <Badge className="bg-purple-500/10 text-purple-500 border-purple-500/20">Poll</Badge>;
            case "submit":
                return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">Submit</Badge>;
            default:
                return null;
        }
    };

    const getStatusIcon = (status: string | null) => {
        switch (status) {
            case "success":
            case "completed":
                return <CheckCircle className="h-4 w-4 text-green-500" />;
            case "failed":
            case "error":
                return <AlertCircle className="h-4 w-4 text-red-500" />;
            case "processing":
                return <Clock className="h-4 w-4 text-blue-500 animate-pulse" />;
            default:
                return <Clock className="h-4 w-4 text-yellow-500" />;
        }
    };

    return (
        <Card variant="glass">
            <CardHeader className="flex flex-row items-center justify-between">
                <div>
                    <CardTitle className="flex items-center gap-2">
                        KIE API Logs
                        <Badge variant="outline" className="ml-2">{filteredLogs.length}</Badge>
                    </CardTitle>
                    <CardDescription>View Kling API callbacks and polling events for debugging</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleRecheckAllStuckJobs}
                        disabled={recheckingAll}
                        className="bg-amber-500/10 border-amber-500/30 text-amber-500 hover:bg-amber-500/20"
                    >
                        {recheckingAll ? (
                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                            <Zap className="h-4 w-4 mr-2" />
                        )}
                        Recheck Stuck
                    </Button>
                    <Button variant="outline" size="sm" onClick={fetchLogs} disabled={loading}>
                        <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
                        Refresh
                    </Button>
                </div>
            </CardHeader>
            <CardContent>
                {/* Search notice from History */}
                {searchParams.get("search") && (
                    <div className="flex items-center gap-2 mb-4 p-3 rounded-lg bg-purple-500/10 border border-purple-500/30">
                        <AlertCircle className="h-4 w-4 text-purple-400" />
                        <span className="text-sm text-purple-300">
                            Filtering logs for failed job: <code className="bg-black/30 px-2 py-0.5 rounded">{searchParams.get("search")?.substring(0, 20)}...</code>
                        </span>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs ml-auto"
                            onClick={clearSearch}
                        >
                            Clear Filter
                        </Button>
                    </div>
                )}

                {/* Search and Filters */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                    <div className="relative flex-1">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                            type="text"
                            placeholder="Search by task ID, log ID, prompt, or model..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className={`w-full pl-10 h-10 bg-white/5 border border-white/10 rounded-lg outline-none focus:ring-1 focus:ring-primary/30 ${searchQuery ? 'pr-10 border-purple-500/50' : 'pr-4'}`}
                        />
                        {searchQuery && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                                onClick={clearSearch}
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        {["all", "callback", "poll", "submit"].map((type) => (
                            <Button
                                key={type}
                                variant={filterType === type ? "default" : "outline"}
                                size="sm"
                                onClick={() => setFilterType(type)}
                                className="capitalize"
                            >
                                {type}
                            </Button>
                        ))}
                    </div>
                </div>

                {loading ? (
                    <div className="flex justify-center py-12">
                        <Loader2 className="h-8 w-8 animate-spin text-primary" />
                    </div>
                ) : filteredLogs.length === 0 ? (
                    <div className="text-center py-12 text-muted-foreground">
                        <AlertCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No KIE logs found</p>
                        <p className="text-sm mt-2">Callbacks will appear here when Kling API sends responses</p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {paginatedLogs.map((log) => (
                            <div
                                key={log.id}
                                className="p-4 rounded-xl bg-muted/30 border border-white/5 hover:bg-muted/50 transition-colors"
                            >
                                {/* Header Row */}
                                <div className="flex items-center justify-between gap-4 mb-2">
                                    <div className="flex items-center gap-3 flex-wrap">
                                        {getEventBadge(log.event_type)}
                                        {getStatusIcon(log.extracted_status)}
                                        <code className="text-xs bg-black/30 px-2 py-1 rounded font-mono">
                                            {log.task_id.substring(0, 16)}...
                                        </code>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="h-6 w-6 p-0"
                                            onClick={() => copyToClipboard(log.task_id, log.id)}
                                        >
                                            {copiedId === log.id ? (
                                                <Check className="h-3 w-3 text-green-500" />
                                            ) : (
                                                <Copy className="h-3 w-3" />
                                            )}
                                        </Button>
                                    </div>
                                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                        <Clock className="h-3 w-3" />
                                        {formatDate(log.created_at)}
                                    </div>
                                </div>

                                {/* Status and URL */}
                                <div className="flex items-center gap-4 mb-2 text-sm">
                                    <span className="text-muted-foreground">Status:</span>
                                    <Badge variant="outline" className="text-xs">
                                        {log.extracted_status || "unknown"}
                                    </Badge>
                                    {log.extracted_url && (
                                        <>
                                            <span className="text-muted-foreground">URL:</span>
                                            <a
                                                href={log.extracted_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-primary hover:underline flex items-center gap-1 truncate max-w-[200px]"
                                            >
                                                {log.extracted_url.substring(0, 30)}...
                                                <ExternalLink className="h-3 w-3" />
                                            </a>
                                        </>
                                    )}
                                </div>

                                {/* Generation Info */}
                                {log.generation_log && (
                                    <div className="text-xs text-muted-foreground mb-2">
                                        <span className="font-medium">{log.generation_log.model}</span>
                                        {" • "}
                                        <span className="italic truncate">"{log.generation_log.prompt?.substring(0, 60)}..."</span>
                                    </div>
                                )}

                                {/* Error Message */}
                                {log.error_message && (
                                    <div className="text-xs text-red-400 bg-red-500/10 rounded px-2 py-1 mb-2">
                                        {log.error_message}
                                    </div>
                                )}

                                {/* Expandable Raw Payload */}
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs h-6 px-2"
                                    onClick={() => toggleExpand(log.id)}
                                >
                                    {expandedLogs.has(log.id) ? (
                                        <>
                                            <ChevronUp className="h-3 w-3 mr-1" />
                                            Hide Raw Payload
                                        </>
                                    ) : (
                                        <>
                                            <ChevronDown className="h-3 w-3 mr-1" />
                                            Show Raw Payload
                                        </>
                                    )}
                                </Button>

                                {expandedLogs.has(log.id) && (
                                    <div className="mt-2 relative">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            className="absolute top-2 right-2 h-6 px-2 text-xs"
                                            onClick={() => copyToClipboard(JSON.stringify(log.raw_payload, null, 2), `payload-${log.id}`)}
                                        >
                                            {copiedId === `payload-${log.id}` ? (
                                                <Check className="h-3 w-3 text-green-500" />
                                            ) : (
                                                <Copy className="h-3 w-3" />
                                            )}
                                        </Button>
                                        <pre className="text-sm bg-slate-900 text-slate-100 p-4 rounded-lg overflow-x-auto max-h-[400px] overflow-y-auto border border-slate-700">
                                            {JSON.stringify(log.raw_payload, null, 2)}
                                        </pre>
                                    </div>
                                )}
                            </div>
                        ))}

                        {/* Pagination Controls */}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between pt-4 border-t border-white/10">
                                <p className="text-sm text-muted-foreground">
                                    แสดง {startIndex + 1}-{Math.min(startIndex + LOGS_PER_PAGE, filteredLogs.length)} จาก {filteredLogs.length} รายการ
                                </p>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        disabled={currentPage === 1}
                                    >
                                        <ChevronLeft className="h-4 w-4" />
                                    </Button>
                                    <span className="text-sm px-3">
                                        {currentPage} / {totalPages}
                                    </span>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        disabled={currentPage === totalPages}
                                    >
                                        <ChevronRight className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default KieLogs;
